<template>
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="text-center mb-12">
            <h1 class="text-4xl font-extrabold text-gray-900 sm:text-5xl md:text-6xl">
                ClaimReady Resources
            </h1>
            <p class="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
                Guides to protecting your wealth before and after a disaster.
            </p>
        </div>

        <div class="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            <UCard v-for="article in articles" :key="article.path" :ui="{ body: 'p-0', header: 'p-0 sm:px-0' }"
                class="flex flex-col h-full overflow-hidden hover:ring-2 hover:ring-primary-500 transition-all duration-200 cursor-pointer"
                @click="router.push(article.path)">
                <template #header>
                    <div class="aspect-w-16 aspect-h-9 w-full relative h-48">
                        <img :src="article.image" :alt="article.title" class="object-cover w-full h-full" />
                    </div>
                </template>

                <div class="p-6 flex flex-col flex-grow">
                    <div class="flex items-center text-sm text-gray-500 mb-2">
                        <UIcon name="i-heroicons-clock" class="mr-1 w-4 h-4" />
                        <span>{{ article.readTime }} read</span>
                    </div>
                    <h3 class="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        {{ article.title }}
                    </h3>
                    <p class="text-gray-600 mb-4 flex-grow line-clamp-3">
                        {{ article.description }}
                    </p>
                    <div class="mt-auto pt-4 flex items-center text-primary-600 font-semibold hover:text-primary-700">
                        Read Article
                        <UIcon name="i-heroicons-arrow-right" class="ml-2 w-4 h-4" />
                    </div>
                </div>
            </UCard>
        </div>
    </div>
</template>

<script setup lang="ts">
const router = useRouter()

definePageMeta({
    layout: 'default'
})

useHead({
    title: 'ClaimReady Resources | Expert Insurance Claim Guides',
    meta: [
        { name: 'description', content: 'Expert guides and resources to help homeowners navigate insurance claims, document their wealth, and avoid settlement traps.' }
    ]
})

const articles = [
    {
        title: 'Insurance Adjuster Secrets: Avoid the "Ballpark" Settlement Trap',
        description: 'Learn how insurance adjusters use your lack of documentation to underpay claims, and how forensic proof changes the negotiation.',
        path: '/resources/insurance-adjuster-secrets-payout',
        image: '/images/resources/insurance-adjuster-secrets.png', // Converted to PNG for consistency with generation
        readTime: '6 min'
    },
    {
        title: 'The 20-Minute Home Audit: How to Document Your Wealth',
        description: 'A strategic guide to documenting your high-value assets in just 20 minutes before fire season begins. Don\'t rely on memory.',
        path: '/resources/20-minute-home-audit',
        image: '/images/resources/20-minute-home-audit.png',
        readTime: '8 min'
    },
    {
        title: 'The $6.99 Insurance Hack: 300,000% ROI on Home Inventories',
        description: 'Stop the "Standard Grade" penalty. See the math on why forensic documentation is the highest-ROI investment you can make.',
        path: '/resources/roi-of-home-insurance-inventory',
        image: '/images/resources/roi-home-inventory.png',
        readTime: '5 min'
    },
    {
        title: 'The Digital Blueprint: Why Cloud Inventories are a Security Risk',
        description: 'Uploading photos of your home to the cloud creates a "shopping list" for hackers. Learn why local-only is the new standard.',
        path: '/resources/the-security-risks-of-cloud-home-inventories',
        image: '/images/resources/privacy-security-header.png',
        readTime: '6 min'
    },
    {
        title: 'New Home? Why the First 30 Days are Critical for Insurance',
        description: 'Buying a home creates a documentation "clean slate." Learn how to prove the condition of your property before problems arise.',
        path: '/resources/first-30-days-home-insurance-inventory-tips',
        image: '/images/resources/first-30-days-home-insurance-inventory-tips.png',
        readTime: '5 min'
    },
    {
        title: 'Why a Spreadsheet Isn\'t Enough: Creating Audit-Ready Evidence',
        description: 'A "master list" in Excel is often legally "hearsay." Learn why adjusters respect professional PDFs over raw spreadsheets.',
        path: '/resources/why-spreadsheet-isnt-enough-for-insurance-claims',
        image: '/images/resources/spreadsheet-vs-pdf-inventory.png',
        readTime: '6 min'
    }
]
</script>